/*
  The public grade report's rules (docs/specs/pop-grade-report.md), kept pure so the
  customer-safe field list is unit-testable at the place the spec puts it.

  The public gradings feed carries more than a customer should see — `office`, `cardValue`,
  `bucket`, `publicLabel`, `stageKey`, `completedOn`, `notes`, `files` (D4). Nothing here
  reads them, and the view builds its rows only from `selectCardInfoRows`, so a field is
  shown only by being added to that function on purpose.
*/

import { CardViewRow } from 'types/gameInfo';

/* The fields the report reads beyond CardViewRow's declared ones. All optional on the wire. */
export type ReportRow = Omit<CardViewRow, 'cardInfo'> & {
  slab?: string | null;
  cardInfo:
    | (CardViewRow['cardInfo'] & {
        language?: string | null;
        cardVariant?: string | null;
      })
    | null;
};

type GradeFields = Pick<
  CardViewRow,
  'grade' | 'finalGrade' | 'centeringGrade' | 'cornersGrade' | 'edgesGrade' | 'surfaceGrade'
>;

/*
  The label's QR code points here, exactly as the printed slab label's does
  (card-grading-admin-ui LabelBackSVG POP_REPORT_URL), so the preview's code scans to the
  same page the real one does.
*/
export const POP_REPORT_URL = 'https://www.pcgpopreport.com/report/';

export const reportUrlFor = (gradedId: string): string => `${POP_REPORT_URL}${gradedId}`;

/*
  Authentic is a card graded for authenticity only. The portal decides it from all four
  sub-grades being 0 (ViewGradingScore); the grade name is checked too so a row whose
  sub-grades are null still reads right.
*/
export const isAuthentic = (row: Omit<GradeFields, 'finalGrade'>): boolean => {
  if ((row.grade || '').trim().toLowerCase() === 'authentic') {
    return true;
  }
  const subs = [row.centeringGrade, row.cornersGrade, row.edgesGrade, row.surfaceGrade];
  return subs.every((value) => value === 0);
};

/* D7: a cert with no final grade yet is "in progress", whatever stage it is at. */
export const isGradeReady = (row: Pick<CardViewRow, 'completed' | 'finalGrade' | 'grade'>): boolean =>
  row.completed === true && row.finalGrade !== null && row.finalGrade !== undefined && !!row.grade;

/* The figure in the grade tile: "A" for Authentic, otherwise the number ("10", "9.5"). */
export const gradeFigure = (row: GradeFields): string =>
  isAuthentic(row) ? 'A' : formatGrade(row.finalGrade);

/* 10.0 → "10", 9.5 → "9.5". */
export const formatGrade = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '';
  }
  return String(Number(value));
};

/* The tile is gold above 9, silver at 9 and below and for Authentic (ViewGradingScore). */
export const isGoldGrade = (row: GradeFields): boolean =>
  !isAuthentic(row) && Number(row.finalGrade) > 9;

/* A sub-grade arc is gold at 9.5 and 10 (ScoreIndicator). */
export const isGoldSubGrade = (value: number): boolean => value === 9.5 || value === 10;

/*
  Flawless is the quad 10: all four sub-grades 10. The grade name says so; the sub-grades are
  checked too, because that is the test the portal's label renderer prints by
  (card-grading-admin-ui LabelFrontSVG getStatus: isFullGold).
*/
export const isFlawless = (row: GradeFields): boolean => {
  if (isAuthentic(row)) {
    return false;
  }
  if ((row.grade || '').trim().toLowerCase() === 'flawless') {
    return true;
  }
  return [row.centeringGrade, row.cornersGrade, row.edgesGrade, row.surfaceGrade].every((value) => value === 10);
};

/*
  The label preview's finish (Robert, 2026-10-26, replacing batch 4's "gold above 9"):

    gold          Flawless only: the brushed gold face of Robert's PDF.
    black-gold    above 9 otherwise (Pristine 10, Gem Mint 10, 9.5): the black label with
                  its lettering, frame, arcs and grade panel in gold.
    black-silver  9 and below, and Authentic: the black label with its parts in silver.

  It is what the portal prints: its label is black, with the accent #D0AA59 gold above 9
  and #89888A grey below, and the all-10s label set apart (LabelFrontSVG getAccentColor).
*/
export type LabelFinish = 'gold' | 'black-gold' | 'black-silver';
export const labelFinish = (row: GradeFields): LabelFinish => {
  if (isFlawless(row)) {
    return 'gold';
  }
  return isGoldGrade(row) ? 'black-gold' : 'black-silver';
};

/*
  The feed says "Standard Slab"; the portal header says "Standard". Anything else is shown
  as the feed has it.
*/
export const slabTypeLabel = (slab: string | null | undefined): string | null => {
  const text = (slab || '').trim();
  if (!text) {
    return null;
  }
  return /^standard( slab)?$/i.test(text) ? 'Standard' : text;
};

/* "2025" and "1999-01-09" both read as the year. */
export const releaseYear = (releaseDate: string | null | undefined): string | null => {
  const match = /^(\d{4})/.exec((releaseDate || '').trim());
  return match ? match[1] : null;
};

export type SubGrade = { title: string; value: number };

export const selectSubGrades = (row: GradeFields): SubGrade[] =>
  [
    { title: 'Centering', value: row.centeringGrade },
    { title: 'Corners', value: row.cornersGrade },
    { title: 'Edges', value: row.edgesGrade },
    { title: 'Surface', value: row.surfaceGrade },
  ].filter((sub) => sub.value !== null && sub.value !== undefined && Number.isFinite(Number(sub.value)));

export type InfoRowKey =
  | 'cert'
  | 'game'
  | 'set'
  | 'card'
  | 'number'
  | 'finish'
  | 'rarity'
  | 'language'
  | 'year';

export type InfoRow = { key: InfoRowKey; label: string; value: string };

/*
  The Card Info rows, in the portal's order, D3's fields only. Empty values are dropped
  rather than shown as "N/A": a customer reading "N/A" asks what is missing.
  `gameLabel` is the resolved display name; the view turns game/set/card into links.
*/
export const selectCardInfoRows = (row: ReportRow, gameLabel: string): InfoRow[] => {
  const info = row.cardInfo;
  const rows: InfoRow[] = [
    { key: 'cert', label: 'Certification Number', value: row.gradedId },
    { key: 'game', label: 'Game', value: gameLabel },
    { key: 'set', label: 'Set', value: row.setName || info?.setName || '' },
    { key: 'card', label: 'Card Name', value: info?.cardName || row.cardName || '' },
    { key: 'number', label: 'Card Number', value: row.cardNumber || info?.number || '' },
    { key: 'finish', label: 'Finish', value: row.finish || '' },
    { key: 'rarity', label: 'Rarity', value: info?.rarity || '' },
    { key: 'language', label: 'Language', value: info?.language || '' },
    { key: 'year', label: 'Release Year', value: releaseYear(info?.releaseDate) || '' },
  ];
  return rows.filter((entry) => entry.value && String(entry.value).trim() !== '');
};

/*
  The label's text lines, built from the same public fields (D5). Front: card name and a
  detail line; back: card name, game, set, finish/rarity, year · language · #number.
*/
export const selectLabelLines = (row: ReportRow, gameLabel: string) => {
  const info = row.cardInfo;
  const cardName = info?.cardName || row.cardName || '';
  const setName = row.setName || info?.setName || '';
  const finishRarity = [row.finish, info?.rarity].filter(Boolean).join(' / ');
  const number = row.cardNumber || info?.number || '';
  const yearLine = [
    releaseYear(info?.releaseDate),
    info?.language,
    number ? `#${number}` : '',
  ]
    .filter(Boolean)
    .join(' ');
  return {
    cardName,
    frontDetail: [setName, finishRarity].filter(Boolean).join(' | '),
    back: [gameLabel, setName, finishRarity, yearLine].filter(Boolean),
  };
};
